/**
 * ReviewStep - Step 6 of view creation wizard
 *
 * Review all settings and create the view
 */

import React, { useMemo } from 'react';
import { Edit, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ViewFormData } from '@/pages/ViewCreatePage';
import { SQLPreview } from './SQLPreview';
import { PerformanceIndicator } from '../PerformanceIndicator';
import { MaterializationHelp, RefreshScheduleHelp } from '../HelpTooltips';
import { calculateChainDepth, hasViewJoins } from '@/lib/utils/viewPerformance';

interface ReviewStepProps {
  formData: ViewFormData;
  onEdit: (step: number) => void;
  onCreate: () => void;
  isLoading: boolean;
  isEditMode?: boolean;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({ formData, onEdit, onCreate, isLoading, isEditMode = false }) => {
  /**
   * Calculate performance metrics for the view configuration
   */
  const performanceMetrics = useMemo(() => {
    const joins = formData.joins || [];
    const chainDepth = calculateChainDepth(joins);
    const hasViews = hasViewJoins(joins);

    return {
      chainDepth,
      joinCount: joins.length,
      hasViewJoins: hasViews,
    };
  }, [formData.joins]);

  /**
   * Generate SQL preview
   */
  const generateSQLPreview = (): string => {
    const parts: string[] = [];

    // SELECT clause
    if (formData.select_all_columns) {
      parts.push('SELECT *');
    } else {
      const columns = formData.columns.map((col) => {
        if (col.transform_expression) {
          return col.column_alias ? `${col.transform_expression} AS ${col.column_alias}` : col.transform_expression;
        }
        return col.column_alias ? `${col.source_column} AS ${col.column_alias}` : col.source_column;
      });
      parts.push(`SELECT ${columns.join(', ')}`);
    }

    // FROM clause
    parts.push(`FROM view_${formData.name}`);

    // TODO: Add WHERE, GROUP BY, HAVING, JOIN clauses when implemented

    return parts.join('\n');
  };

  return (
    <div className="space-y-6">
      {/* Performance Indicator - Show if JOINs exist */}
      {performanceMetrics.joinCount > 0 && (
        <PerformanceIndicator {...performanceMetrics} />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Basic Info */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Basic Information</CardTitle>
            <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(1)}>
              <Edit className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>
              <span className="ml-2 font-medium">{formData.name}</span>
            </div>
            {formData.description && (
              <div>
                <span className="text-muted-foreground">Description:</span>
                <p className="mt-1 text-xs">{formData.description}</p>
              </div>
            )}
            {formData.tags.length > 0 && (
              <div>
                <span className="text-muted-foreground">Tags:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* File Selection Rules */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">File Selection</CardTitle>
            <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(2)}>
              <Edit className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Rules:</span>
              <span className="ml-2 font-medium">{formData.rules.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Logic:</span>
              <Badge variant="outline" className="ml-2 text-xs">
                {formData.rule_logic}
              </Badge>
            </div>
            {formData.rules.length > 0 && (
              <div className="mt-2 space-y-1">
                {formData.rules.slice(0, 3).map((rule, index) => (
                  <div key={rule.id} className="text-xs text-muted-foreground">
                    {index + 1}. {rule.type.replace('_', ' ').toUpperCase()}
                  </div>
                ))}
                {formData.rules.length > 3 && (
                  <div className="text-xs text-muted-foreground">
                    + {formData.rules.length - 3} more
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Column Configuration */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Columns</CardTitle>
            <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(3)}>
              <Edit className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {formData.select_all_columns ? (
              <div>
                <Badge variant="secondary">All Columns (*)</Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  Includes all columns from matched files
                </p>
              </div>
            ) : (
              <div>
                <span className="text-muted-foreground">Selected:</span>
                <span className="ml-2 font-medium">{formData.columns.length} columns</span>
                {formData.columns.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {formData.columns.slice(0, 3).map((col) => (
                      <div key={col.id} className="text-xs text-muted-foreground">
                        {col.column_alias || col.source_column}
                      </div>
                    ))}
                    {formData.columns.length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        + {formData.columns.length - 3} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Refresh Settings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Refresh Settings
              <RefreshScheduleHelp />
            </CardTitle>
            <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(5)}>
              <Edit className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Type:</span>
              <Badge variant={formData.view_type === 'materialized' ? 'default' : 'secondary'} className="ml-auto">
                {formData.view_type}
              </Badge>
              {formData.view_type === 'materialized' && <MaterializationHelp />}
            </div>
            <div>
              <span className="text-muted-foreground">Auto-refresh:</span>
              <span className="ml-2">{formData.is_auto_refresh ? 'Enabled' : 'Disabled'}</span>
            </div>
            {formData.is_auto_refresh && (
              <div>
                <span className="text-muted-foreground">Schedule:</span>
                <span className="ml-2 capitalize">{formData.refresh_schedule?.replace('_', ' ')}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SQL Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Generated SQL (Preview)</CardTitle>
        </CardHeader>
        <CardContent>
          <SQLPreview sql={generateSQLPreview()} />
          <p className="text-xs text-muted-foreground mt-2">
            This is a simplified preview. The actual SQL generated by the backend may include additional
            optimizations and transformations.
          </p>
        </CardContent>
      </Card>

      {/* Warnings */}
      {formData.view_type === 'materialized' && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Materialized views require additional storage space. The initial materialization may take
            several minutes depending on data size.
          </AlertDescription>
        </Alert>
      )}

      {/* Create/Save Button */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onEdit(5)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Settings
        </Button>
        <Button type="button" onClick={onCreate} disabled={isLoading} className="min-w-32">
          {isLoading ? (
            isEditMode ? 'Saving...' : 'Creating...'
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              {isEditMode ? 'Save Changes' : 'Create View'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
